import _, { isArray } from "lodash";
import {
  AdditionalInfoDataTypes,
  DynamicComponent,
  ValueType,
} from "us.common/constants";

/**
 * @function
 * @description - To get the matching component for the input form
 * @param selectedFieldName - Selected field name
 * @param {Array} metaDataList - List of options
 * @return {*}  {DynamicComponent} - The dynamic component type
 */
export const getValueComponent = (
  selectedFieldName: any,
  metaDataList: Array<any>
): DynamicComponent => {
  try {
    const { dataType } = _?.find(metaDataList, {
      label: selectedFieldName,
    });

    switch (dataType) {
      case AdditionalInfoDataTypes.STRING:
        return DynamicComponent.Input;
      case AdditionalInfoDataTypes.INT:
        return DynamicComponent.InputNumber;
      case AdditionalInfoDataTypes.DECIMAL:
        return DynamicComponent.InputAmount;
      case AdditionalInfoDataTypes.DATE_TIME:
        return DynamicComponent.DatePicker;
      case AdditionalInfoDataTypes.BOOLEAN:
        return DynamicComponent.Switch;
      case AdditionalInfoDataTypes.CUSTOM:
        return DynamicComponent.Select;
      default:
        return DynamicComponent.Input;
    }
  } catch (error) {
    return DynamicComponent.Input;
  }
};

/**
 * @function
 * @description - Get value source as a list for the selected field
 * @param selectedFieldName - Selected field name
 * @param {Array} metaDataList - List of options
 * @param {Array} bmdDataTypeValues SP data list
 * @return {Array<any>}  value source of the selected field
 */
export const getExtendedFieldValueSource = (
  selectedFieldName: any,
  metaDataList: Array<any>,
  bmdDataTypeValues: Array<string>
): Array<any> => {
  try {
    const { valueSource, valueType } = _?.find(metaDataList, {
      label: selectedFieldName,
    });
    if (valueType === ValueType.SP) {
      const values =
        Array.isArray(bmdDataTypeValues) && bmdDataTypeValues?.length > 0
          ? bmdDataTypeValues.map((item: string) => ({
              label: item.trim(),
              value: item.trim(),
            }))
          : [];
      return values;
    } else {
      if (isArray(valueSource) || valueSource?.length > 0) {
        if (isArray(valueSource)) {
          return valueSource;
        } else {
          const values =
            valueSource && valueSource.length > 0
              ? valueSource.split(",")?.map((item: string) => ({
                  label: item.trim(),
                  value: item.trim(),
                }))
              : [];
          return values;
        }
      } else {
        return [];
      }
    }
  } catch (error) {
    return [];
  }
};

/**
 * @function
 * @description - To get the matching value type
 * @param selectedFieldName - Selected field name
 * @param {Array} metaDataList - List of options
 * @return {any}  data type of the selected field
 */
export const getSelectedMetaDataInfo = (
  selectedFieldName: any,
  metaDataList: Array<any>
): any => {
  try {
    const metaData = _?.find(metaDataList, {
      label: selectedFieldName,
    });
    return metaData;
  } catch (error) {
    return "";
  }
};

/**
 * Retrieves the placeholder value based on the provided data type.
 *
 * @param dataType - The type of data for which the placeholder value is required.
 *                   It should correspond to one of the values in `AdditionalInfoDataTypes`.
 * @returns A string representing the placeholder value for the specified data type.
 *          - For `AdditionalInfoDataTypes.DECIMAL`: Returns a message prompting for a decimal value.
 *          - For `AdditionalInfoDataTypes.STRING`: Returns a message prompting for a string value.
 *          - For `AdditionalInfoDataTypes.INT`: Returns a message prompting for an integer value.
 *          - For `AdditionalInfoDataTypes.CUSTOM`: Returns a message prompting to select a value from a custom list.
 *          - For any other data type: Returns a default message for extended field values.
 */
export const getDynamicPlaceholderValue = (dataType: string) => {
  switch (dataType) {
    case AdditionalInfoDataTypes.DECIMAL:
      return "US.COLLECTION.VALIDATIONS:INVALID.PLEASE_ENTER_A_DECIMAL_VALUE";
    case AdditionalInfoDataTypes.STRING:
      return "US.COLLECTION.VALIDATIONS:INVALID.PLEASE_ENTER_A_STRING_VALUE";
    case AdditionalInfoDataTypes.INT:
      return "US.COLLECTION.VALIDATIONS:INVALID.PLEASE_ENTER_AN_INTEGER_VALUE";
    case AdditionalInfoDataTypes.CUSTOM:
      return "US.COLLECTION.VALIDATIONS:INVALID.PLEASE_SELECT_A_VALUE_FROM_THE_CUSTOM_LIST";
    default:
      return "US.COLLECTION.COMMON:EXTENDED_FIELD.VALUE";
  }
};
